import clsx from 'clsx';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Route, Routes, useLocation} from 'react-router-dom';
import AuthRoutes from '@/routes/auth';
import MAIN_ROUTES_LIST from '@/routes/main/mainRoutesList';
import featureFlagsMapping from '@/config/featureFlagsMapping';
import {
  HIDE_MENU_ROUTES,
  HIDE_BRAND_HEADER_ROUTES,
  HIDE_SUBSCRIPTION_BANNER_ROUTES,
} from '@/config/routesConstants';
import {HIDE_MENU_SUBSCRIPTIONS} from '@/config/subscriptionConstants';
import CampaignIsLiveModal from '@/views/campaigns/brief/components/campaign-brief/components/campaign-is-live-modal/CampaignIsLiveModal';
import Banner from '@/components/banner';
import BrandHeader from '@/components/brand-header';
import EmberIframe from '@/components/ember-iframe';
import EmptyBrandState from '@/components/empty-brand-state';
import GlobalMessagingPanel from '@/components/global-messaging-panel';
import Menu from '@/components/menu';
import SubscriptionExpirationBanner from '@/components/subscription-expiration-banner';
import useAgencyQuery from '@/hooks/queries/use-agency-query';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useSubscriptionQuery from '@/hooks/queries/use-subscription-query';
import useBrandId from '@/hooks/use-brand-id';
import useElementHeight from '@/hooks/use-element-height';
import useFifaRevampRedirect from '@/hooks/use-fifa-revamp-redirect';
import useIsMobile from '@/hooks/use-is-mobile';
import useLocalStorage from '@/hooks/use-local-storage';
import useNavigation from '@/hooks/use-navigation';
import useRouteDocumentTitle from '@/hooks/use-route-document-title/useRouteDocumentTitle';
import useSetBrandIdFromLoggedInDashboardUser from '@/hooks/use-set-brand-id-from-logged-in-dashboard-user';
import useSetEmberIframeStyles from '@/hooks/use-set-ember-iframe-styles';
import useToken from '@/hooks/use-token';
import useEmberIframeStyleStore, {
  emberIframeStyleActions,
} from '@/stores/emberIframeStyleStore';
import useEmberModalsStackStore, {
  emberModalsStackActions,
} from '@/stores/emberModalsStackStore';
import useRedirectAfterAuthStore, {
  redirectAfterAuthActions,
} from '@/stores/redirectAfterAuthStore';
import styles from './MainRoutes.module.scss';

const IS_MENU_COLLAPSED_LOCAL_STORAGE_KEY = 'isMenuCollapsed';

export type MainRoutesProps = {
  flags: Record<string, boolean>;
};

function MainRoutes(props: MainRoutesProps) {
  const {flags} = props;
  const isMobile = useIsMobile();
  const {isAuthenticated} = useToken(true);
  const location = useLocation();

  useRouteDocumentTitle();

  const brandId = useBrandId();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const {modalsStack: emberModalsStack} = useEmberModalsStackStore();
  const [headerBannerShown, setHeaderBannerShown] = useState(false);
  const [isMenuCollapsed, setIsMenuCollapsed] = useLocalStorage(
    IS_MENU_COLLAPSED_LOCAL_STORAGE_KEY,
    true
  );

  const {headerHeight: initialHeaderHeight, menuHeight: initialMenuHeight} =
    useEmberIframeStyleStore();

  const navigation = useNavigation();

  const {redirectUrlAfterAuth} = useRedirectAfterAuthStore();

  const {dashboardUser}: any = useDashboardUserQuery({
    enabled: isAuthenticated,
    overrideEnabled: !brandId && isAuthenticated,
  });
  const {currentBrand} = useAgencyQuery({brandId: brandId || ''});

  useSetBrandIdFromLoggedInDashboardUser();

  // FIFA Revamp: Redirect to /creator-crm if user is on a disallowed route
  useFifaRevampRedirect(flags.fifaRevamp);

  useEffect(() => {
    if (isAuthenticated && redirectUrlAfterAuth && dashboardUser.loaded) {
      if (
        redirectUrlAfterAuth.includes(dashboardUser.currentBrandId) ||
        dashboardUser.isAdmin
      ) {
        navigation.navigate(redirectUrlAfterAuth);
      }
      redirectAfterAuthActions.setRedirectUrlAfterAuth(null);
    }
  }, [isAuthenticated, redirectUrlAfterAuth, dashboardUser.loaded]);

  const testRouteLocation = useCallback(
    (route: string) => new RegExp(route, 'i').test(location.pathname),
    [location.pathname]
  );

  const {subscription} = useSubscriptionQuery({
    brandId,
  });

  const {elementRef: headerRef, elementHeight: headerHeight} = useElementHeight(
    [subscription],
    initialHeaderHeight
  );
  const {elementRef: menuRef, elementHeight: menuHeight} = useElementHeight(
    [subscription, isMobile],
    initialMenuHeight
  );

  const emberIframeRef = useRef<HTMLIFrameElement>(null);

  const getZIndexClass = () => {
    if (emberModalsStack.length) {
      return styles.zeroIndex;
    }
    return '';
  };

  useSetEmberIframeStyles(emberIframeRef, iframeLoaded, location);

  // Check if user has no brands assigned (SSO users with no brand access)
  const hasNoBrands =
    isAuthenticated &&
    dashboardUser.loaded &&
    !dashboardUser.currentBrandId &&
    (!dashboardUser.brands || dashboardUser.brands.length === 0);

  const {showBrandHeader, showMenu, showSubscriptionExpirationBanner} =
    useMemo(() => {
      return {
        showBrandHeader:
          isAuthenticated && !HIDE_BRAND_HEADER_ROUTES.find(testRouteLocation),
        showMenu:
          isAuthenticated &&
          !hasNoBrands &&
          !HIDE_MENU_ROUTES.find(testRouteLocation) &&
          !HIDE_MENU_SUBSCRIPTIONS.includes(subscription.subscriptionType),
        showSubscriptionExpirationBanner:
          isAuthenticated &&
          !hasNoBrands &&
          !HIDE_SUBSCRIPTION_BANNER_ROUTES.find(testRouteLocation),
      };
    }, [
      isAuthenticated,
      hasNoBrands,
      location.pathname,
      dashboardUser.loaded,
      subscription.subscriptionType,
    ]);

  const sidebarWidth = useMemo(() => {
    if (!showMenu) {
      return 0;
    }
    if (isMenuCollapsed) {
      return parseInt(styles.sidenavCollapsedWidth, 10);
    }
    return parseInt(styles.sidenavFullWidth, 10);
  }, [showMenu, isMenuCollapsed]);

  useEffect(() => {
    emberIframeStyleActions.setDependantDimensions({
      headerHeight,
      menuHeight,
      sidebarWidth,
    });
  }, [headerHeight, menuHeight, sidebarWidth]);

  useEffect(() => {
    const removeModalListener = EmberIframe.listen(
      'modal',
      (modalNameOrFalse) => {
        emberModalsStackActions.setEmberModalsStack(modalNameOrFalse);
      }
    );
    return removeModalListener;
  }, []);

  const menuContainerClassNames = clsx(
    styles.menuContainer,
    getZIndexClass(),
    headerBannerShown && styles.headerBannerShown
  );

  const renderMenu = () =>
    showMenu ? (
      <div
        className={menuContainerClassNames}
        style={{margin: flags.contentLab ? '0px' : '16px 0 16px 16px'}}
      >
        <Menu
          user={dashboardUser}
          currentBrand={currentBrand}
          flags={flags}
          subscription={subscription}
          ref={menuRef}
          isCollapsed={isMenuCollapsed}
          handleCollapse={() => setIsMenuCollapsed(!isMenuCollapsed)}
        />
      </div>
    ) : null;

  const renderHeader = () => (
    <div className={`${styles.headerContainer} ${getZIndexClass()}`}>
      <div className={styles.headerWrapper} ref={headerRef}>
        {showBrandHeader ? (
          <BrandHeader user={dashboardUser} subscription={subscription} />
        ) : null}
        {showSubscriptionExpirationBanner ? (
          <SubscriptionExpirationBanner
            subscription={subscription}
            setHeaderBannerShown={setHeaderBannerShown}
          />
        ) : null}
      </div>
    </div>
  );

  const contentContainerClassNames = clsx(
    styles.contentContainer,
    !showMenu && styles.hideMenu,
    headerBannerShown && styles.headerBannerShown
  );

  const routes = MAIN_ROUTES_LIST.filter(({name}) => {
    const flagName = featureFlagsMapping.routes[name];
    const featureFlagNotExists = !(flagName in flags);

    // temporary check
    // if reactCampaigns flag is on, hide organic campaign stats route -> it renders under the new React Campaign route
    // if reactCampaigns flag is off, show organic campaign stats route -> it renders as is without the campaign tabs
    // TODO: remove this check along with the removal of the reactCampaigns flag
    if (name === 'campaign-stats-organic') {
      return !flags.reactCampaigns;
    }

    return featureFlagNotExists || flags[flagName] === true;
  });

  const renderContent = () => {
    // Show empty state for SSO users with no brands assigned
    if (hasNoBrands) {
      return (
        <div className={contentContainerClassNames}>
          <EmptyBrandState
            organizationOwnerEmail={
              dashboardUser.agency?.organizationOwner?.email
            }
          />
        </div>
      );
    }

    return (
      <div className={contentContainerClassNames}>
        <Routes>
          {AuthRoutes({flags})}
          <Route
            path="/*"
            element={
              <EmberIframe
                iframeRef={emberIframeRef}
                setIframeLoaded={setIframeLoaded}
              />
            }
          />
          {routes.map(({name, path, Component}) => (
            <Route
              key={name}
              path={path}
              element={
                <Component flags={flags} dashboardUser={dashboardUser} />
              }
            />
          ))}
        </Routes>
      </div>
    );
  };

  const appContainerClassNames = clsx(
    styles.appContainer,
    !emberIframeRef.current && styles.reactRouteContainer,
    !showMenu && styles.fullscreen
  );

  return (
    <div className={styles.appWrapper}>
      <Banner />
      <CampaignIsLiveModal />
      <GlobalMessagingPanel />
      <div className={styles.container}>
        <div className={appContainerClassNames}>
          {renderHeader()}
          {renderMenu()}
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default MainRoutes;
